import {
  DlgDataAssetCar,
  DlgUserProfile,
  DlgDataQuote,
  DlgDataPolicyInfo
} from 'dlg-dolphin/dist/lib/interfaces/dlg-data';

import { DateTimeHelpers } from '@global/infrastructure/helpers/date-time-helpers';


export interface MapperStrategy {
  mapFeature(data: any): any;
}

export class AnalyticsMapper {
  constructor(private readonly mapperStrategy: MapperStrategy) {
  }

  mapFeature(data: any): any {
    return this.mapperStrategy.mapFeature(data);
  }
}

export class MapperFactory {
  static create(featureName: string): AnalyticsMapper {
    // tslint:disable-next-line: no-use-before-declare
    return new FeatureToAnalyticsMap()[featureName];
  }
}

export class PolicyMapper implements MapperStrategy {
  mapFeature(data: any): DlgDataPolicyInfo {
    return {
      ...(data.name && { name: data.name }),
      ...(data.price && { price: data.price }),
      ...(data.last_year_price && { last_year_price: data.last_year_price }),
      quantity: 1,
      ...(data.policyNumber && { policyNumber: data.policyNumber }),
      ...(data.cover_level && { cover_level: data.cover_level }),
      ...(data.renewal_bound && { renewal_bound: data.renewal_bound }),
      ...(data.policy_type && { policy_type: data.policy_type }),
      ...(data.add_ons && { add_ons: data.add_ons }),
    };
  }
}

export class QuoteMapper implements MapperStrategy {
  mapFeature(data: any): DlgDataQuote {
    return {
      ...(data.price && { landing: { price: data.price } }),
      ...(data.price && { final: { price: data.price } }),
      ...(data.apr && { apr: data.apr }),
      ...(data.payment_type && { payment_type: data.payment_type }),
      ...(data.card_type && { payment_card_type: data.card_type }),
      ...(data.changes && { changes: data.changes }),
      ...(data.quoteID && { formId: data.quoteID }),
      ...(data.existing_policy_renewal_date && {
        existing_policy_renewal_date: data.existing_policy_renewal_date,
      }),
      ...(data.renew && { renew: data.renew }),
      ...(data.price && { value: data.price }),
    };
  }
}

export class CarMapper implements MapperStrategy {

  private static getNumberOfModifications(data: any): number {
    return data?.length ?? 0;
  }

  static getObjectToDate(dateValue) {
    if (!isNaN(Date.parse(dateValue))) {
      const time = Date.parse(dateValue);
      return new Date(time).toString();
    } else {
      return null;
    }
  }

  mapFeature(data: any): DlgDataAssetCar {
    return {
      make: data?.make ?? '',
      model: data?.model ?? '',
      year: data?.year ?? '',
      modified: data?.modifications > 0 ? 'Yes' : 'No',
      modifications: data?.modifications
        ? CarMapper.getNumberOfModifications(data.modifications)
        : undefined,
      purchased: data?.isVehiclePurchased ? 'Yes' : 'No',
      purchased_date: CarMapper.getObjectToDate(data?.purchaseDate),
      est_mileage: data?.annualMileage ?? undefined,
      registered_keeper: data?.registeredKeeper ?? ''
    };
  }
}

export class ProfileMapper implements MapperStrategy {

  private static getDriverAge(dateOfBirth: any): number {
    const today = new Date();
    const todayArray = [
      today.getDate().toString(),
      today.getMonth().toString() + 1,
      today.getFullYear().toString()
    ];
    return DateTimeHelpers.getDifferenceInYears(dateOfBirth, todayArray);
  }

  mapFeature(data: any): DlgUserProfile[] {
    const users = [];
    data.forEach((user) => {
      const objUser = {
        title: user.title,
        sso_id: user.sso_id,
        DoB: user.dob,
        age: user.dob ? ProfileMapper.getDriverAge(user.dob) : undefined,
        employment_status: user.employmentStatus,
        gender: user?.gender ?? '',
        industry: user?.industry ?? '',
        marital_status: user?.maritalStatus ?? '',
        occupation: user?.occupation ?? '',
        convictions: user?.convictions ? user.convictions.length : 0,
        claims: user.claims ? user.claims.length : 0,
        email: user?.emailAddress ?? '',
        postcode: user?.postCode ?? '',
        postcode_first: user.postCode ? user.postCode.split(' ')[0] : '',
        license_type: user.licenseType,
        uk_resident: user.ukResident,
        uk_resident_since: user.ukResidentSince,
        years_license_held: user.licenseHeldFor,
        user_type: user.userType,
        policy: user?.policy,
      };
      users.push(objUser);
    });
    return users;
  }
}

export class FeatureToAnalyticsMap {
  'yourCarPerm' = new AnalyticsMapper(new CarMapper());
  'yourCarTemp' = new AnalyticsMapper(new CarMapper());
  'yourDetails' = new AnalyticsMapper(new ProfileMapper());
  'policy' = new AnalyticsMapper(new PolicyMapper());
  'quote' = new AnalyticsMapper(new QuoteMapper());
  'additionalDrivers' = new AnalyticsMapper(new ProfileMapper());
}
